import * as React from 'react'
import * as Redux from 'react-redux'
import { styled } from 'next-yak'

import * as api from '@owl-nest/api-client/latest'
import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import * as shadow from '@owl-nest/shadow'
import * as plume from '@ulule/owl-kit-components/next'
import { useDramaticallyInefficientGlobalCss, css } from '@owl-nest/yak/extra-runtime'

import { Like } from './Like'
import * as projectModel from '../../../models/project'
import { RootState } from '../../../../reducers'
import { SuccessMessage } from './SuccessMessage'
import { SplashTitle } from '../components/SplashTitle'
import { Title } from './Title'
import { likeProject } from '../../../../actions/project'
import { useDisplayMode } from '../../../hooks/useDisplayMode'

export function Home(): React.ReactElement {
  const dispatch = Redux.useDispatch()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const defaultFollowers = [
    api.UserRole.Editor,
    api.UserRole.Fan,
    api.UserRole.Moderator,
    api.UserRole.Owner,
    api.UserRole.Supporter,
  ]
  const auth = useAuth()
  const tracking = shadow.useTracking()

  const displayMode = useDisplayMode(project)

  const [isUserFan, setIsUserFan] = React.useState(getInitialIsUserFan())
  const [isLoading, setIsLoading] = React.useState(false)

  const showTitle = auth.type !== 'existing' && !isLoading && !isUserFan

  const urlSearchParams = new URLSearchParams(window.location.search)
  const isIframeFromUrl = urlSearchParams.get('iframe') === 'true'

  if (isIframeFromUrl && window?.zE) {
    window.zE('webWidget', 'hide')
  }

  useDramaticallyInefficientGlobalCss(css`
    html {
      overflow: ${isIframeFromUrl ? 'hidden' : 'auto'};
    }
  `)

  return (
    <HomeWrapper>
      {showTitle && <Title />}

      <Block>
        {isIframeFromUrl ? (
          <Like handleLike={handleLike} />
        ) : isLoading ? (
          <CenterSpinner>
            <plume.Spinner />
          </CenterSpinner>
        ) : isUserFan ? (
          <SuccessMessage auth={auth} />
        ) : (
          <Like handleLike={handleLike} />
        )}
      </Block>
    </HomeWrapper>
  )

  async function handleLike(user: api.AuthenticatedUser<any>): Promise<void> {
    setIsLoading(true)
    await like(user)
    setIsLoading(false)
  }

  async function like(user: api.AuthenticatedUser<any>): Promise<void> {
    await dispatch(likeProject(project.id))
    setIsUserFan(true)

    tracking.lead.track({
      leadSource: user.is_guest ? 'coming_source_guest_user' : 'coming_source_known_user',
    })

    if (auth.type === 'loggedin-as-guest') {
      // We logout the user here because guest user haven't acces and it makes errors
      // on other pages or on refresh.
      // We just need a logged user for handle like and logout him after it's ok.
      auth.logout()
    }
  }

  function getInitialIsUserFan() {
    if (displayMode === 'preview') {
      return false
    }
    return project.user_role && defaultFollowers.includes(project.user_role)
  }
}

const Block = styled.div`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 4px;

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    margin-bottom: 30px;
  }
`

const CenterSpinner = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 186px;
  text-align: center;
  width: 100%;
`

const HomeWrapper = styled.div`
  padding: 0 16px 16px;

  ${SplashTitle} {
    margin-bottom: 25px;
  }

  ${plume.styles.heading.S} {
    margin: 0 auto;
    margin-bottom: 25px;
    text-align: center;
    width: fit-content;
  }

  @media screen and ${plume.BREAKPOINTS.LAPTOP} {
    padding: 0;

    ${plume.styles.heading.S} {
      margin-bottom: 28px;
    }
  }
`
